import React, { Fragment } from 'react';
import { graphql, Link } from 'gatsby';
import PropTypes from 'prop-types';
import kebabCase from 'lodash.kebabcase';
import cloneDeep from 'lodash.clonedeep';
import uniqBy from 'lodash.uniqby';
import {
  PageHeader,
  FootNote,
  TaxFilter,
  SimpleDataTable,
  DangerousWrapper,
  Section,
  makeBreadcrumb
} from '@budget-monorepo/shared-ui';
import Layout from '../components/Layout';
import HelmetData from '../components/HelmetData';

const TaxExpenditure = (props) => {
  const { location, data, pageContext } = props;
  const {
    content,
    site,
    filter,
    footnotes
  } = data;
  // eslint-disable-next-line camelcase
  const { modifiedTime, mg_mod_date, mg_mod_datetime } = content.parent;

  const { baseSectionURL } = pageContext;
  // Define the breadcrumb structure of the of the tax expenditure template.
  const { pageTitle, breadcrumbprops } = makeBreadcrumb({
    href: baseSectionURL,
    text: 'Tax Expenditure Budget',
    sections: [content.taxType, content.title]
  });

  // Set the props for layout component
  const layoutProps = {
    activeIndex: 0,
    pre: () => <TaxFilter {...{ location, filter }} />,
    above: () => <PageHeader title={content.title} category={content.budgetSection} />,
    pageTitle,
    breadcrumbprops,
    dataTimeStamp: modifiedTime,
    buildTimeStamp: site.buildTime
  };

  // Pull mapped tax item data to same level as headers.
  const getInnerData = (table) => {
    const procTable = cloneDeep(table);
    const allFootnotes = [];
    const tableData = procTable.data.map((row) => {
      const { footnote } = row.data;
      const record = {
        ...row.data,
        taxItem: (
          <React.Fragment>
            {
              /* eslint-disable jsx-a11y/anchor-is-valid */
            }
            <Link to={`${baseSectionURL}${kebabCase(row.data.taxType)}/${kebabCase(row.data.taxExpenditure)}/${kebabCase(row.data.taxItemNumber)}`}>
              {row.data.taxItemNumber}
            </Link>
            <DangerousWrapper htmlTag="span" rawHtml={`<br />${row.data.taxItemName}`} />
          </React.Fragment>
        )
      };
      if (footnote) {
        footnote.forEach((note) => allFootnotes.push(note));
      }
      return({
        ...record
      });
    });
    procTable.data = tableData;
    return{ detailsTable: { ...procTable }, allFootnotes };
  };

  const { detailsTable, allFootnotes } = getInnerData(content.detailsTable);
  const uniqueFootnotes = uniqBy(allFootnotes, 'index');

  // metadata for the page
  const metaTitle = `${content.budgetSection}: ${content.title}`;
  const metaDescription = `Tax expenditure data for ${content.title} under ${content.taxType}.`;
  return(
    <Fragment>
      <HelmetData {...{
        metaTitle,
        metaDescription,
        mg_mod_date,
        mg_mod_datetime
      }}
      />
      <Layout {...layoutProps}>
        <Section>
          <SimpleDataTable title={`${content.budgetSection}: ${content.title} summary table`} table={content.summaryTable} maxColWidth={100} mantissa={1} download={`${location.pathname}-overview`} />
        </Section>
        <Section>
          <SimpleDataTable title={`${content.budgetSection}: ${content.title} details table`} table={detailsTable} maxColWidth={300} mantissa={1} download={`${location.pathname}-details`} />
        </Section>
        <Section>
          { /* If there are footnotes for the table, render these footnotes and their
               at the bottom of the page beneath the table. */
            footnotes && uniqueFootnotes && uniqueFootnotes.map((note) => (
              <FootNote key={`footnote.${note.index}`} index={note.index}>
                {footnotes.footnotes[parseInt(note.index, 10) - 1] && footnotes.footnotes[parseInt(note.index, 10) - 1].text}
              </FootNote>
            ))}
        </Section>
      </Layout>
    </Fragment>
  );
};

TaxExpenditure.propTypes = {
  data: PropTypes.object,
  location: PropTypes.object,
  pageContext: PropTypes.object
};

export default TaxExpenditure;

export const query = graphql`
  query($slug: String!) {
    site {
      ...SiteMetadata
    }
    content: taxExpenditure(fields: { slug: { eq: $slug } }) {
      title
      budgetSection
      taxType
      summaryTable {
        headers {
          name
          type
          align
          value
        }
        data {
          taxType
          taxExpenditureName
          taxExpenditureNumber
          fyFourYearsAgo
          fyThreeYearsAgo
          fyTwoYearsAgo
          fyOneYearsAgo
          fyThisYear
          footnote
        }
      }
      detailsTable {
        headers {
          name
          type
          align
          value
        }
        data {
          data {
            taxItemNumber
            taxItemName
            taxType
            taxExpenditure
            description
            origin
            fyThisYear
            footnote {
              field
              index
            }
          }
        }
      }
      # Get the last modified timestamp for the file powering the page
      ...TaxExpenditureDataTimeStamp
    }
    filter: taxItemJson {
      data {
        taxType
        taxExpenditure
        taxItemNumber
        taxItemName
      }
    }
    # Get all of the footnotes for the tax items.
    footnotes: taxItemJson {
      footnotes {
        index
        text
      }
    }
  }
`;
